import { useCallback } from 'react'
import { Box, InputAdornment, SvgIcon, TextField, Typography } from '@mui/material'
import { PropTypes } from 'prop-types'
import { useController, useFormContext } from 'react-hook-form'

import ErrorIcon from '@hapana/shared/web/styles/assets/svgs/exclamation-circle-small.svg'

import { styles } from './text-area.styles'
import { FIELD_VARIANTS } from './text-area.constants'

const EndAdornment = () => (
  <InputAdornment position="end">
    <SvgIcon sx={styles.errorIcon} component={ErrorIcon} />
  </InputAdornment>
)

const TextArea = ({
  label,
  helperText,
  readOnly,
  maxLength,
  disabled,
  required,
  error,
  sx,
  rows,
  name,
  inputStyles,
  labelStyles,
  rules,
  characterCounterColor,
  shrink,
}) => {
  const { control } = useFormContext()
  const {
    field: { value, onChange, ref },
  } = useController({
    control,
    defaultValue: '',
    rules,
    name,
  })

  const CharacterCount = useCallback(
    () => (
      <Box component="span" sx={styles.characterCounterContainer}>
        {helperText && (
          <Typography sx={[disabled && styles.helperTextDisabled]} variant="small" component="span">
            {helperText}
          </Typography>
        )}
        {maxLength > 0 && (
          <Typography
            variant="small"
            sx={styles.characterCounter}
            component="span"
            color={characterCounterColor}
          >
            {value?.toString().length || 0} / {maxLength}
          </Typography>
        )}
      </Box>
    ),
    [helperText, disabled, maxLength, value]
  )

  return (
    <TextField
      inputRef={ref}
      error={error}
      value={value}
      name={name}
      required={required}
      label={label}
      disabled={disabled}
      onChange={onChange}
      multiline
      sx={[styles.textField, ...(Array.isArray(sx) ? sx : [sx])]}
      rows={rows}
      variant={FIELD_VARIANTS.OUTLINED}
      helperText={<CharacterCount />}
      inputProps={{ readOnly, maxLength }}
      InputProps={{
        sx: [styles.input, ...(Array.isArray(inputStyles) ? inputStyles : [inputStyles])],
        endAdornment: error && value?.length > 0 && <EndAdornment />,
      }}
      InputLabelProps={{
        sx: [styles.label, ...(Array.isArray(labelStyles) ? labelStyles : [labelStyles])],
        shrink: shrink ?? undefined,
      }}
      FormHelperTextProps={{ sx: styles.helperText }}
    />
  )
}

TextArea.propTypes = {
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  helperText: PropTypes.string,
  readOnly: PropTypes.bool,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  required: PropTypes.bool,
  rows: PropTypes.number,
  sx: PropTypes.shape({ maxWidth: PropTypes.number }),
  name: PropTypes.string,
  inputStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  labelStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  rules: PropTypes.object,
  characterCounterColor: PropTypes.string,
  shrink: PropTypes.bool,
}

TextArea.defaultProps = {
  sx: {},
  required: false,
  disabled: false,
  readOnly: false,
  rows: 4,
  inputStyles: {},
  rules: {},
}

export default TextArea
